import type { JSX } from 'react';
import type { ClientResource, DuplicateUseCaseBody } from '@voxler/contracts';
import { assertNever } from '@voxler/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { CallingCodesField, SenderField } from '../SmsSettingsInputs';
import { useToast } from '../toast';
import { ApiHttpError, internalSend } from '../../lib/api';
import {
  DEFAULT_CALLING_CODES,
  formatCallingCodes,
  readSmsSettings,
  smsSettingsOf,
  suggestSender,
  type UseCase,
  type UseCaseKind,
} from '../../lib/use-cases';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { Field, FieldError, Fields2 } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';

/** Translates the API error of a use-case duplication into a French message. */
function duplicateUseCaseErrorMessage(error: unknown): string {
  if (error instanceof ApiHttpError) {
    switch (error.status) {
      case 409:
        return 'Ce client possède déjà un cas d’usage avec cette clé. Choisissez une autre clé.';
      case 404:
        return 'Client ou cas d’usage introuvable.';
      case 422:
        return error.detail ?? 'Réglages de la copie invalides.';
      default:
        return 'Échec de la duplication du cas d’usage.';
    }
  }
  return 'Échec de la duplication du cas d’usage.';
}

const DUPLICATE_SUBTITLE_AI =
  'Copie la configuration (type, modèle, mode, paramètres) et le prix par défaut vers un autre client. Les deux cas d’usage sont ensuite indépendants.';

/** What a duplication copies, per kind: an SMS copy usually shows the target client's brand. */
const DUPLICATE_SUBTITLES: Record<UseCaseKind, string> = {
  text: DUPLICATE_SUBTITLE_AI,
  image: DUPLICATE_SUBTITLE_AI,
  sms: 'Copie la configuration (service SMS, expéditeur, indicatifs autorisés) et le prix par défaut vers un autre client. L’expéditeur affiché est en général la marque du client cible. Les deux cas d’usage sont ensuite indépendants.',
};

/**
 * Copy of a use case to another client (D-UC.5). For an SMS use case, the copy
 * takes its own sender (prefilled from the target client's name) and allowed
 * calling codes (lot 17). Mounted only while open.
 */
export function DuplicateUseCaseDialog({
  source,
  clients,
  onClose,
  onDuplicated,
}: {
  source: UseCase;
  clients: readonly ClientResource[];
  onClose: () => void;
  onDuplicated: (useCaseId: string) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const sourceSms = smsSettingsOf(source);

  const [targetClientId, setTargetClientId] = useState('');
  const [key, setKey] = useState(source.key);
  const [sender, setSender] = useState(sourceSms?.sender ?? '');
  // Once the operator types a sender, choosing another client no longer replaces it.
  const [senderEdited, setSenderEdited] = useState(false);
  const [callingCodes, setCallingCodes] = useState(
    sourceSms === null ? DEFAULT_CALLING_CODES : formatCallingCodes(sourceSms.allowedCallingCodes),
  );
  const [error, setError] = useState('');

  const changeTarget = (clientId: string): void => {
    setTargetClientId(clientId);
    if (!senderEdited) {
      const target = clients.find((client) => client.id === clientId);
      setSender(target === undefined ? (sourceSms?.sender ?? '') : suggestSender(target.name));
    }
  };

  const changeSender = (value: string): void => {
    setSender(value);
    setSenderEdited(value.trim().length > 0);
  };

  const duplicate = useMutation({
    mutationFn: (body: DuplicateUseCaseBody) =>
      internalSend<UseCase>('POST', `/internal/use-cases/${source.id}/duplicate`, body),
    onSuccess: (copy) => {
      void queryClient.invalidateQueries({ queryKey: ['use-cases'] });
      notify('Cas d’usage dupliqué : vérifiez son prix');
      onDuplicated(copy.id);
    },
    onError: (failure) => {
      setError(duplicateUseCaseErrorMessage(failure));
    },
  });

  const submit = (): void => {
    setError('');
    if (targetClientId.length === 0) {
      setError('Le client cible est obligatoire.');
      return;
    }
    if (key.trim().length === 0) {
      setError('La clé est obligatoire.');
      return;
    }
    const { kind } = source;
    switch (kind) {
      case 'text':
      case 'image':
        duplicate.mutate({ targetClientId, key: key.trim() });
        return;
      case 'sms': {
        const settings = readSmsSettings(sender, callingCodes);
        if (!settings.ok) {
          setError(settings.error);
          return;
        }
        duplicate.mutate({ targetClientId, key: key.trim(), sms: settings.value });
        return;
      }
      default:
        return assertNever(kind);
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHead
          title={
            <>
              Dupliquer <span className="font-mono">{source.key}</span>
            </>
          }
          description={DUPLICATE_SUBTITLES[source.kind]}
        />
        <DialogBody>
          <Fields2>
            <Field label="Client cible" required>
              <NativeSelect
                value={targetClientId}
                onChange={(event) => {
                  changeTarget(event.target.value);
                }}
              >
                <option value="">Choisir un client</option>
                {clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field label="Clé" required>
              <Input
                className="font-mono"
                value={key}
                onChange={(event) => {
                  setKey(event.target.value);
                }}
                placeholder="invoice-extraction"
              />
            </Field>
          </Fields2>
          {source.kind === 'sms' ? (
            <>
              <SenderField value={sender} onChange={changeSender} />
              <CallingCodesField value={callingCodes} onChange={setCallingCodes} />
            </>
          ) : null}
          <Callout variant="info">
            Gardez la même clé pour que les ERP des deux clients utilisent la même. Les remises et
            prix propres à un client ne sont pas copiés : ajustez le prix du nouveau cas d’usage
            après la duplication.
          </Callout>
          {error.length > 0 ? <FieldError>{error}</FieldError> : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="button" size="lg" onClick={submit} disabled={duplicate.isPending}>
            Dupliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
